import { Action, combineReducers, configureStore } from '@reduxjs/toolkit';
import { setupListeners } from '@reduxjs/toolkit/query';
import {
  agentMapsApi,
  agentStatusApi,
  apiSharingPartnerApi,
  authApi,
  commonApi,
  confirmReflectionRequestApi,
  createTicketFromExcelApi,
  customersApi,
  customerSupportInformationApi,
  excelsApi,
  filesApi,
  followingRequestApi,
  frequentlyAskedQuestionApi,
  incorrectrecordinglocationApi,
  internalAnnouncementsApi,
  internalChatApi,
  notesApi,
  notificationsApi,
  omisInfoRetrievalApi,
  omniChannelApi,
  optionsApi,
  organizationUnitsApi,
  organizationUnitTicketDefaultAssignsApi,
  permissionsApi,
  qaIntegrationApi,
  reasonsApi,
  requestForm,
  rolesApi,
  screenRecorderApi,
  serviceInstructionsApi,
  serviceTypeCMISApi,
  serviceTypesApi,
  shiftsApi,
  softPhoneApi,
  systemConfigsApi,
  ticketCancelReasonsApi,
  ticketSamplesApi,
  toolsApi,
  userGroupsApi,
  usersApi,
  vipCustomersApi,
  workingSchedulesApi,
  contactSentimentsApi
} from 'services';
import {
  authReducer,
  authSoftPhoneReducer,
  customerSupportSessionReducer,
  internalChatReducer,
  omniChatReducer,
  shiftsReducer,
  usersReducer
} from './features';
import { customerLookupApi } from 'services/customer-lookup';
import { ticketsApi } from 'services/tickets';
import { channelsApi } from 'services/channels';
import { administrativeUnitsApi } from 'services/administrative-units';
import { trackingTicketsProcessingApi } from '../services/tracking-ticket-processing';
import { collaborationRequestApi } from '../services/collaboration-request';
import { agentMapReducer } from './features/agent-map';
import { customerGroupsApi } from 'services/customer-groups';
import { contactsApi } from 'services/contacts/contacts.api';
import { constructionSiteManagement } from '../services/request-form/construction-site-management.api';

const combinedReducer = combineReducers({
  [authApi.reducerPath]: authApi.reducer,
  [usersApi.reducerPath]: usersApi.reducer,
  [organizationUnitsApi.reducerPath]: organizationUnitsApi.reducer,
  [userGroupsApi.reducerPath]: userGroupsApi.reducer,
  [permissionsApi.reducerPath]: permissionsApi.reducer,
  [optionsApi.reducerPath]: optionsApi.reducer,
  [filesApi.reducerPath]: filesApi.reducer,
  [rolesApi.reducerPath]: rolesApi.reducer,
  [customerSupportInformationApi.reducerPath]: customerSupportInformationApi.reducer,
  [serviceTypesApi.reducerPath]: serviceTypesApi.reducer,
  [frequentlyAskedQuestionApi.reducerPath]: frequentlyAskedQuestionApi.reducer,
  [serviceInstructionsApi.reducerPath]: serviceInstructionsApi.reducer,
  [workingSchedulesApi.reducerPath]: workingSchedulesApi.reducer,
  [shiftsApi.reducerPath]: shiftsApi.reducer,
  [reasonsApi.reducerPath]: reasonsApi.reducer,
  [vipCustomersApi.reducerPath]: vipCustomersApi.reducer,
  [internalChatApi.reducerPath]: internalChatApi.reducer,
  [notesApi.reducerPath]: notesApi.reducer,
  [ticketCancelReasonsApi.reducerPath]: ticketCancelReasonsApi.reducer,
  [ticketSamplesApi.reducerPath]: ticketSamplesApi.reducer,
  [internalAnnouncementsApi.reducerPath]: internalAnnouncementsApi.reducer,
  [notificationsApi.reducerPath]: notificationsApi.reducer,
  [systemConfigsApi.reducerPath]: systemConfigsApi.reducer,
  [agentMapsApi.reducerPath]: agentMapsApi.reducer,
  [customerLookupApi.reducerPath]: customerLookupApi.reducer,
  [ticketsApi.reducerPath]: ticketsApi.reducer,
  [channelsApi.reducerPath]: channelsApi.reducer,
  [administrativeUnitsApi.reducerPath]: administrativeUnitsApi.reducer,
  [customersApi.reducerPath]: customersApi.reducer,
  [apiSharingPartnerApi.reducerPath]: apiSharingPartnerApi.reducer,
  [organizationUnitTicketDefaultAssignsApi.reducerPath]: organizationUnitTicketDefaultAssignsApi.reducer,
  [requestForm.reducerPath]: requestForm.reducer,
  [omniChannelApi.reducerPath]: omniChannelApi.reducer,
  [serviceTypeCMISApi.reducerPath]: serviceTypeCMISApi.reducer,
  [softPhoneApi.reducerPath]: softPhoneApi.reducer,
  [excelsApi.reducerPath]: excelsApi.reducer,
  [agentStatusApi.reducerPath]: agentStatusApi.reducer,
  [trackingTicketsProcessingApi.reducerPath]: trackingTicketsProcessingApi.reducer,
  [collaborationRequestApi.reducerPath]: collaborationRequestApi.reducer,
  [omisInfoRetrievalApi.reducerPath]: omisInfoRetrievalApi.reducer,
  [incorrectrecordinglocationApi.reducerPath]: incorrectrecordinglocationApi.reducer,
  [followingRequestApi.reducerPath]: followingRequestApi.reducer,
  [confirmReflectionRequestApi.reducerPath]: confirmReflectionRequestApi.reducer,
  [customerGroupsApi.reducerPath]: customerGroupsApi.reducer,
  [commonApi.reducerPath]: commonApi.reducer,
  [contactsApi.reducerPath]: contactsApi.reducer,
  [screenRecorderApi.reducerPath]: screenRecorderApi.reducer, // Thêm screen recorder API vào store
  [qaIntegrationApi.reducerPath]: qaIntegrationApi.reducer,
  [createTicketFromExcelApi.reducerPath]: createTicketFromExcelApi.reducer,
  [constructionSiteManagement.reducerPath]: constructionSiteManagement.reducer,
  [toolsApi.reducerPath]: toolsApi.reducer,
  [contactSentimentsApi.reducerPath]: contactSentimentsApi.reducer,

  auth: authReducer,
  customerSupportSession: customerSupportSessionReducer,
  users: usersReducer,
  shifts: shiftsReducer,
  internalChat: internalChatReducer,
  authSoftPhone: authSoftPhoneReducer,
  omniChat: omniChatReducer,
  agentMap: agentMapReducer // Thêm agent-map reducer vào store
});
const rootReducer = (state: any, action: Action) => {
  if (action.type === 'RESET') {
    //We are calling this RESET, but call what you like!
    state = {};
  }
  return combinedReducer(state, action);
};
export const store = configureStore({
  reducer: rootReducer,
  // Adding the api middleware enables caching, invalidation, polling, and other features of RTK Query
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware().concat(
      usersApi.middleware,
      authApi.middleware,
      organizationUnitsApi.middleware,
      userGroupsApi.middleware,
      permissionsApi.middleware,
      optionsApi.middleware,
      filesApi.middleware,
      rolesApi.middleware,
      customerSupportInformationApi.middleware,
      serviceTypesApi.middleware,
      frequentlyAskedQuestionApi.middleware,
      serviceInstructionsApi.middleware,
      workingSchedulesApi.middleware,
      shiftsApi.middleware,
      reasonsApi.middleware,
      vipCustomersApi.middleware,
      internalChatApi.middleware,
      notesApi.middleware,
      ticketCancelReasonsApi.middleware,
      ticketSamplesApi.middleware,
      internalAnnouncementsApi.middleware,
      notificationsApi.middleware,
      systemConfigsApi.middleware,
      agentMapsApi.middleware,
      customerLookupApi.middleware,
      ticketsApi.middleware,
      channelsApi.middleware,
      administrativeUnitsApi.middleware,
      customersApi.middleware,
      apiSharingPartnerApi.middleware,
      organizationUnitTicketDefaultAssignsApi.middleware,
      requestForm.middleware,
      omniChannelApi.middleware,
      serviceTypeCMISApi.middleware,
      softPhoneApi.middleware,
      excelsApi.middleware,
      agentStatusApi.middleware,
      trackingTicketsProcessingApi.middleware,
      collaborationRequestApi.middleware,
      omisInfoRetrievalApi.middleware,
      incorrectrecordinglocationApi.middleware,
      followingRequestApi.middleware,
      confirmReflectionRequestApi.middleware,
      customerGroupsApi.middleware,
      commonApi.middleware,
      contactsApi.middleware,
      screenRecorderApi.middleware,
      qaIntegrationApi.middleware,
      createTicketFromExcelApi.middleware,
      constructionSiteManagement.middleware,
      toolsApi.middleware,
      contactSentimentsApi.middleware
    )
});

setupListeners(store.dispatch);

// Infer the `RootState` and `AppDispatch` types from the store itself
export type RootState = ReturnType<typeof store.getState>;
// Inferred type: {posts: PostsState, comments: CommentsState, users: UsersState}
export type AppDispatch = typeof store.dispatch;
